import {
	chooseBrandControlColor,
	getContrastingTextColor,
	Glyphs,
	LiveField,
	WidgetIconWrapper,
	WidgetInputQuickSwitch,
} from '@/ui'
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd'
import ctl from '@netlify/classnames-template-literals'
import i18next from 'i18next'
import { useAtom, useAtomValue } from 'jotai'
import { CSSProperties, memo, ReactNode, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 } from 'uuid'

import { logger } from '@/ui'
import { CampaignFormQuestion } from '@/ui'
import { campaignDetailsBrandingAtom, formAtom, formStatusAtom } from '@/ui'
import { CampaignScoreValues } from '../../views/shared/campaign-resources'
import { NewOptionButton, NewQuestionButton } from './buttons'
import { generateMaxSelectionsOptions, QUESTION_TYPE_OPTIONS, REQUIRED_OPTIONS, WEIGHT_OPTIONS } from './constants'
import { DragHandle } from './drag-handle'
import { MaxSelectionsIcon, QuestionWeightIcon, RequiredIcon } from './icons'
import { OptionRow } from './option-row'

const DESCENDING_SCORES: CampaignScoreValues[] = [
	CampaignScoreValues.EXCELLENT,
	CampaignScoreValues.GOOD,
	CampaignScoreValues.OKAY,
	CampaignScoreValues.POOR,
	CampaignScoreValues.BAD,
]

function getDescendingScore(index: number): CampaignScoreValues {
	return DESCENDING_SCORES[Math.min(index, DESCENDING_SCORES.length - 1)]
}

function getDefaultOptionLabel(): string {
	return i18next.t('editor:simulator.form.newOption')
}

type TrackQuestionEditedParams = {
	field: string
	questionId: string
	questions: CampaignFormQuestion[]
	positionOverride?: number
	previousValue?: string | number
	newValue?: string | number
}

function trackQuestionEdited(
	{ field, questionId, questions, positionOverride, previousValue, newValue }: TrackQuestionEditedParams,
): void {
	logger.track('Editor: Form question edited', {
		data: {
			field,
			question_id: questionId,
			question_position: positionOverride ?? questions.findIndex((q) => q.id === questionId),
			total_questions: questions.length,
			...(previousValue !== undefined && { previous_value: previousValue }),
			...(newValue !== undefined && { new_value: newValue }),
		},
	})
}

const labelEditTimers = new Map<string, ReturnType<typeof setTimeout>>()

function trackQuestionEditedDebounced(params: TrackQuestionEditedParams): void {
	const key = `${params.questionId}:${params.field}`
	const existing = labelEditTimers.get(key)
	if (existing) { clearTimeout(existing) }
	labelEditTimers.set(
		key,
		setTimeout(() => {
			labelEditTimers.delete(key)
			trackQuestionEdited(params)
		}, 2000),
	)
}

export type FormSimulatorProps = {}

export function OuterFormSimulator({}: FormSimulatorProps) {
	const { t } = useTranslation('editor')
	const formStatus = useAtomValue(formStatusAtom)
	const isGenerating = formStatus === 'generating'
	const isToolExecuting = formStatus === 'tool-executing'
	const isLoading = formStatus === 'pending' || formStatus === 'idle'

	if (isGenerating || isLoading) {
		return (
			<div className="flex items-center justify-center h-40 animate-pulse">
				{isGenerating ? t('simulator.form.generating') : t('simulator.form.loading')}
			</div>
		)
	}

	return (
		<div className={isToolExecuting ? 'animate-pulse' : ''}>
			<FormSimulator />
		</div>
	)
}

export const FormSimulator = memo(function FormSimulator() {
	const { t } = useTranslation('editor')
	const branding = useAtomValue(campaignDetailsBrandingAtom)
	const formStatus = useAtomValue(formStatusAtom)
	const [form, setForm] = useAtom(formAtom)

	// Mirrors form-viewer behaviour (ENG-4824): when the user's brand background
	// is too pale to be visible on the white preview canvas, fall back to the
	// foreground so question badges, option rings, and the submit button stay
	// visible.
	const brandControlColor = chooseBrandControlColor(branding?.background, branding?.foreground)

	const isGenerating = formStatus === 'generating'
	const isLoading = formStatus === 'pending' || formStatus === 'idle'

	const handleLabelChange = useCallback((id: string, label: string) => {
		if (form?.questions) {
			trackQuestionEditedDebounced({ field: 'label', questionId: id, questions: form.questions })
		}
		setForm((form) => ({
			...form!,
			questions: form!.questions.map((question) => (
				question.id === id ? { ...question, label } : question
			)),
		}))
	}, [setForm, form?.questions])

	const handleOptionLabelChange = useCallback((questionId: string, optionId: string, label: string) => {
		if (form?.questions) {
			trackQuestionEditedDebounced({ field: 'options_label', questionId, questions: form.questions })
		}
		setForm((form) => ({
			...form!,
			questions: form!.questions.map((question) => (
				question.id === questionId
					? {
						...question,
						optionsModified: true,
						options: question.options?.map((option) => (
							option.id === optionId ? { ...option, label } : option
						)),
					}
					: question
			)),
		}))
	}, [setForm, form?.questions])

	const handleOptionEligibilityChange = useCallback(
		(questionId: string, optionId: string, isDisqualifying: boolean) => {
			setForm((form) => ({
				...form!,
				questions: form!.questions.map((question) => (
					question.id === questionId
						? {
							...question,
							optionsModified: true,
							options: question.options?.map((option) => (
								option.id === optionId
									? { ...option, isDisqualifying: isDisqualifying }
									: option
							)),
						}
						: question
				)),
			}))
		},
		[setForm],
	)

	const handleOptionConsentTypeChange = useCallback(
		(questionId: string, optionId: string, consentType: string) => {
			setForm((form) => ({
				...form!,
				questions: form!.questions.map((question) => (
					question.id === questionId
						? {
							...question,
							options: question.options?.map((option) => (
								option.id === optionId
									? { ...option, consentType: consentType }
									: option
							)),
						}
						: question
				)),
			}))
		},
		[setForm],
	)

	const handleOptionScoreChange = useCallback((questionId: string, optionId: string, score: CampaignScoreValues) => {
		setForm((form) => ({
			...form!,
			questions: form!.questions.map((question) => (
				question.id === questionId
					? {
						...question,
						optionsModified: true,
						scores: question.scores?.map((s) => (
							s.optionId === optionId && s.questionId === questionId
								? { ...s, scoreValue: score }
								: s
						)),
					}
					: question
			)),
		}))
	}, [setForm])

	const handleOptionDelete = useCallback((questionId: string, optionId: string) => {
		if (form?.questions) {
			trackQuestionEdited({ field: 'options_delete', questionId, questions: form.questions })
		}
		setForm((form) => ({
			...form!,
			questions: form!.questions.map((question) => (
				question.id === questionId
					? {
						...question,
						options: question.options?.filter((option) => option.id !== optionId),
					}
					: question
			)),
		}))
	}, [setForm, form?.questions])

	const handleQuestionDelete = useCallback((questionId: string) => {
		if (form?.questions) {
			const position = form.questions.findIndex((q) => q.id === questionId)
			logger.track('Editor: Form question deleted', {
				data: {
					question_id: questionId,
					question_position: position === -1 ? null : position + 1,
					total_questions: form.questions.length,
				},
			})
		}
		setForm((form) => ({
			...form!,
			questions: form!.questions.filter((question) => question.id !== questionId),
		}))
	}, [setForm, form?.questions])

	const handleNewQuestion = useCallback((index: number) => {
		const questionId = v4()
		trackQuestionEdited({
			field: 'added',
			questionId,
			questions: form?.questions ?? [],
			positionOverride: index + 1,
		})
		setForm((form) => {
			const defaultLabel = getDefaultOptionLabel()
			const defaultOptions = Array.from({ length: 3 }, () => ({
				id: v4(),
				label: defaultLabel,
				isDisqualifying: false,
				consentType: null,
			}))

			const newQuestions = [...form!.questions]
			newQuestions.splice(index + 1, 0, {
				id: questionId,
				label: i18next.t('editor:simulator.form.newQuestion'),
				type: 'select',
				attribute: null,
				subtype: null,
				weight: 1,
				options: defaultOptions,
				scores: defaultOptions.map((option, optionIndex) => ({
					questionId,
					optionId: option.id,
					scoreValue: getDescendingScore(optionIndex),
				})),
			})

			return {
				...form!,
				questions: newQuestions,
			}
		})
	}, [setForm, form?.questions.length])

	const handleNewOption = useCallback((questionId: string, index: number) => {
		if (form?.questions) {
			trackQuestionEdited({ field: 'options_add', questionId, questions: form.questions })
		}
		setForm((form) => {
			const targetQuestion = form!.questions.find((q) => q.id === questionId)
			const existingOptions = targetQuestion?.options ?? []
			const newOptions = [...existingOptions]
			newOptions.splice(index + 1, 0, {
				id: v4(),
				label: getDefaultOptionLabel(),
				isDisqualifying: false,
				consentType: targetQuestion?.attribute === 'contact_consent' ? 'positive' : null,
			})

			const isMultiSelect = targetQuestion?.subtype === 'multiple'
			const previousScores = targetQuestion?.scores ?? []
			const newScores = newOptions.map((option) => ({
				questionId,
				optionId: option.id,
				scoreValue: previousScores.find((score) => score.optionId === option.id)?.scoreValue
					?? (isMultiSelect ? CampaignScoreValues.EXCELLENT : getDescendingScore(existingOptions.length)),
			}))

			return {
				...form!,
				questions: form!.questions.map((question) => (
					question.id === questionId
						? { ...question, options: newOptions, scores: newScores }
						: question
				)),
			}
		})
	}, [setForm, form?.questions])

	const handleQuestionDragEnd = useCallback((result: DropResult) => {
		if (!result.destination) {
			return
		}

		const reorderedQuestion = form?.questions[result.source.index]
		if (reorderedQuestion && form?.questions) {
			logger.track('Editor: Form question reordered', {
				data: {
					question_id: reorderedQuestion.id,
					from_position: result.source.index + 1,
					to_position: result.destination.index + 1,
					total_questions: form.questions.length,
				},
			})
		}

		setForm((form) => {
			const newQuestions = [...form!.questions]
			const [reorderedItem] = newQuestions.splice(result.source.index, 1)
			// result.destination shouldn't be null becausewe echecked that above
			newQuestions.splice(result.destination?.index ?? 0, 0, reorderedItem)

			return {
				...form!,
				questions: newQuestions,
			}
		})
	}, [setForm, form?.questions])

	const handleOptionDragEnd = useCallback((result: DropResult) => {
		if (!result.destination) {
			return
		}

		const questionId = result.source.droppableId
		if (form?.questions) {
			trackQuestionEdited({ field: 'options_reorder', questionId, questions: form.questions })
		}

		setForm((form) => {
			const newOptions = [
				...form!.questions.find((question) => question.id === questionId)?.options ?? [],
			]
			const [reorderedItem] = newOptions.splice(result.source.index, 1)
			// result.destination shouldn't be null becausewe echecked that above
			newOptions.splice(result.destination?.index ?? 0, 0, reorderedItem)

			return {
				...form!,
				questions: form!.questions.map((question) => (
					question.id === questionId
						? { ...question, options: newOptions }
						: question
				)),
			}
		})
	}, [setForm, form?.questions])

	const handleQuestionWeightChange = useCallback((questionId: string, updatedQuestion: CampaignFormQuestion) => {
		if (form?.questions) {
			const previousQuestion = form.questions.find((q) => q.id === questionId)
			trackQuestionEdited({
				field: 'weight',
				questionId,
				questions: form.questions,
				previousValue: previousQuestion?.weight ?? 1,
				newValue: updatedQuestion.weight ?? 1,
			})
		}
		setForm((form) => ({
			...form!,
			questions: form!.questions.map((question) => (
				question.id === questionId ? updatedQuestion : question
			)),
		}))
	}, [setForm, form?.questions])

	const handleQuestionTypeChange = useCallback((questionId: string, typeValue: string) => {
		if (form?.questions) {
			trackQuestionEdited({ field: 'type', questionId, questions: form.questions })
		}
		setForm((form) => ({
			...form!,
			questions: form!.questions.map((question) => {
				if (question.id !== questionId) { return question }

				if (typeValue === 'open_text') {
					// Switching to open text: clear options and scores
					return {
						...question,
						type: 'open_text',
						subtype: null,
						options: undefined,
						scores: undefined,
					}
				} else if (typeValue === 'select_multiple') {
					// Switching to multi-select - preserve existing validation rules (e.g. 'required', 'max:N')
					const existingOptions = question.options ?? [{
						id: v4(),
						label: getDefaultOptionLabel(),
						isDisqualifying: false,
						consentType: question.attribute === 'contact_consent' ? 'positive' : null,
					}]
					// Keep existing rules (required, max:N), filter out legacy min: rules
					const preservedRules = (question.validationRules ?? []).filter(
						(rule) => !rule.startsWith('min:'),
					)
					return {
						...question,
						type: 'select',
						subtype: 'multiple',
						options: existingOptions,
						validationRules: preservedRules.length > 0 ? preservedRules : null,
						scores: existingOptions.map((option) => ({
							questionId,
							optionId: option.id,
							scoreValue: question.optionsModified
								? (question.scores?.find((score) => score.optionId === option.id)?.scoreValue
									?? CampaignScoreValues.EXCELLENT)
								: CampaignScoreValues.EXCELLENT,
						})),
					}
				} else {
					// Switching to single select - preserve existing validation rules (e.g. 'required')
					const existingOptions = question.options ?? [{
						id: v4(),
						label: getDefaultOptionLabel(),
						isDisqualifying: false,
						consentType: question.attribute === 'contact_consent' ? 'positive' : null,
					}]
					// Keep existing rules (required), filter out multi-select specific rules (min:, max:)
					const preservedRules = (question.validationRules ?? []).filter(
						(rule) => !rule.startsWith('min:') && !rule.startsWith('max:'),
					)
					return {
						...question,
						type: 'select',
						subtype: null,
						options: existingOptions,
						validationRules: preservedRules.length > 0 ? preservedRules : null,
						scores: existingOptions.map((opt, optIndex) => ({
							questionId: questionId,
							optionId: opt.id,
							scoreValue: question.optionsModified
								? (question.scores?.find((s) => s.optionId === opt.id)?.scoreValue
									?? CampaignScoreValues.EXCELLENT)
								: getDescendingScore(optIndex),
						})),
					}
				}
			}),
		}))
	}, [setForm, form?.questions])

	const handleRequiredChange = useCallback((questionId: string, isRequired: boolean) => {
		if (form?.questions) {
			trackQuestionEdited({ field: 'required', questionId, questions: form.questions })
		}
		setForm((form) => ({
			...form!,
			questions: form!.questions.map((question) => {
				if (question.id !== questionId) { return question }

				const currentRules = question.validationRules ?? []
				const hasRequired = currentRules.includes('required')

				let newRules: string[]
				if (isRequired && !hasRequired) {
					newRules = ['required', ...currentRules]
				} else if (!isRequired && hasRequired) {
					newRules = currentRules.filter((rule) => rule !== 'required')
				} else {
					newRules = currentRules
				}

				return {
					...question,
					validationRules: newRules.length > 0 ? newRules : null,
				}
			}),
		}))
	}, [setForm, form?.questions])

	const handleMaxSelectionsChange = useCallback((questionId: string, maxValue: number) => {
		if (form?.questions) {
			trackQuestionEdited({ field: 'max_selections', questionId, questions: form.questions })
		}
		setForm((form) => ({
			...form!,
			questions: form!.questions.map((question) => {
				if (question.id !== questionId) { return question }

				const currentRules = question.validationRules ?? []
				// Remove any existing max rule
				const rulesWithoutMax = currentRules.filter((rule) => !rule.startsWith('max:'))
				// Add new max rule
				const newRules = [...rulesWithoutMax, `max:${maxValue}`]

				return {
					...question,
					validationRules: newRules,
				}
			}),
		}))
	}, [setForm, form?.questions])

	const getItemStyle = useCallback((isDragging: boolean, draggableStyle: CSSProperties) => ({
		userSelect: 'none' as const,

		// styles we need to apply on draggables
		...draggableStyle,
	}), [])

	if (!form) {
		return null
	}

	return (
		<div
			style={{
				width: '500px',
				fontFamily: 'Onest',
			}}
			className="ring perspective-dramatic ring-grey-200 bg-white flex flex-col  rounded-sm shadow-lg p-6"
		>
			<div className="flex flex-col gap-4 group/form mt-10">
				{isGenerating || isLoading
					? <>/* No-op safeguard; this is checked in the outer wrapper.*/</>
					: (
						<>
							<DragDropContext onDragEnd={handleQuestionDragEnd}>
								<Droppable
									droppableId="questions"
									renderClone={(provided, snapshot, rubric) => (
										<div
											{...provided.draggableProps}
											{...provided.dragHandleProps}
											ref={provided.innerRef}
										>
											<Question
												dragHandle={
													<div
														className="absolute -left-4 h-5 w-5 top-[1rem] -translate-y-1/2 text-grey-500 flex items-center justify-center"
														{...provided.dragHandleProps}
													>
														<DragHandle />
													</div>
												}
												question={form.questions[rubric.source.index]}
												index={rubric.source.index}
												handleLabelChange={handleLabelChange}
												handleQuestionDelete={handleQuestionDelete}
												handleNewOption={handleNewOption}
												handleOptionDragEnd={handleOptionDragEnd}
												getItemStyle={getItemStyle}
												handleOptionLabelChange={handleOptionLabelChange}
												handleOptionEligibilityChange={handleOptionEligibilityChange}
												handleOptionScoreChange={handleOptionScoreChange}
												handleOptionConsentTypeChange={handleOptionConsentTypeChange}
												handleOptionDelete={handleOptionDelete}
												handleNewQuestion={handleNewQuestion}
												handleQuestionWeightChange={handleQuestionWeightChange}
												handleQuestionTypeChange={handleQuestionTypeChange}
												handleRequiredChange={handleRequiredChange}
												handleMaxSelectionsChange={handleMaxSelectionsChange}
												backgroundColor={brandControlColor}
											/>
										</div>
									)}
								>
									{(provided) => (
										<div ref={provided.innerRef} {...provided.droppableProps}>
											{form.questions.map((question, index) => (
												<Draggable key={question.id} draggableId={question.id} index={index}>
													{(provided) => (
														<div
															ref={provided.innerRef}
															{...provided.draggableProps}
															className="relative group/question"
														>
															<Question
																dragHandle={
																	<div
																		className="absolute -left-6 top-[1rem] -translate-y-1/2 text-transparent  group-hover/question:text-grey-400 flex items-center justify-center"
																		{...provided.dragHandleProps}
																	>
																		<div className="">
																			<DragHandle />
																		</div>
																	</div>
																}
																question={question}
																index={index}
																handleLabelChange={handleLabelChange}
																handleQuestionDelete={handleQuestionDelete}
																handleNewOption={handleNewOption}
																handleOptionDragEnd={handleOptionDragEnd}
																getItemStyle={getItemStyle}
																handleOptionLabelChange={handleOptionLabelChange}
																handleOptionEligibilityChange={handleOptionEligibilityChange}
																handleOptionDelete={handleOptionDelete}
																handleOptionScoreChange={handleOptionScoreChange}
																handleOptionConsentTypeChange={handleOptionConsentTypeChange}
																handleNewQuestion={handleNewQuestion}
																handleQuestionWeightChange={handleQuestionWeightChange}
																handleQuestionTypeChange={handleQuestionTypeChange}
																handleRequiredChange={handleRequiredChange}
																handleMaxSelectionsChange={handleMaxSelectionsChange}
																backgroundColor={brandControlColor}
															/>
														</div>
													)}
												</Draggable>
											))}
											{provided.placeholder}
										</div>
									)}
								</Droppable>
							</DragDropContext>
							<div className="flex justify-center mt-4">
								<div
									className="rounded-sm px-5 py-3 max-w-fit font-semibold"
									style={{
										backgroundColor: brandControlColor,
										color: getContrastingTextColor(brandControlColor),
									}}
								>
									Submit
								</div>
							</div>
						</>
					)}
			</div>
		</div>
	)
})

const Question = memo(function Question(
	{
		question,
		index,
		handleLabelChange,
		handleQuestionDelete,
		handleNewOption,
		handleOptionDragEnd,
		handleOptionLabelChange,
		handleOptionEligibilityChange,
		handleOptionScoreChange,
		handleOptionConsentTypeChange,
		handleOptionDelete,
		handleNewQuestion,
		handleQuestionWeightChange,
		handleQuestionTypeChange,
		handleRequiredChange,
		handleMaxSelectionsChange,
		getItemStyle,
		dragHandle,
		backgroundColor,
	}: {
		question: CampaignFormQuestion
		index: number
		handleLabelChange: (id: string, label: string) => void
		handleQuestionDelete: (id: string) => void
		handleNewOption: (id: string, index: number) => void
		handleOptionDragEnd: (result: DropResult) => void
		handleOptionLabelChange: (questionId: string, optionId: string, label: string) => void
		handleOptionEligibilityChange: (questionId: string, optionId: string, isDisqualifying: boolean) => void
		handleOptionScoreChange: (questionId: string, optionId: string, score: CampaignScoreValues) => void
		handleOptionConsentTypeChange: (questionId: string, optionId: string, consentType: string) => void
		handleOptionDelete: (questionId: string, optionId: string) => void
		handleNewQuestion: (index: number) => void
		handleQuestionWeightChange: (questionId: string, updatedQuestion: CampaignFormQuestion) => void
		handleQuestionTypeChange: (questionId: string, typeValue: string) => void
		handleRequiredChange: (questionId: string, isRequired: boolean) => void
		handleMaxSelectionsChange: (questionId: string, maxValue: number) => void
		getItemStyle: (isDragging: boolean, draggableStyle: CSSProperties) => CSSProperties
		dragHandle: ReactNode
		backgroundColor: string
	},
) {
	const { t } = useTranslation('editor')
	const isRequired = question.attribute === 'email'
		|| question.attribute === 'name'
		|| question.attribute === 'phone'
		|| question.attribute === 'contact_consent'

	const onAddOptionTop = useCallback(() => handleNewOption(question.id, -1), [handleNewOption, question.id])
	const onAddQuestion = useCallback(() => handleNewQuestion(index), [handleNewQuestion, index])
	const onDeleteQuestion = useCallback(() => handleQuestionDelete(question.id), [handleQuestionDelete, question.id])

	return (
		<div
			key={question.id}
			className={ctl(`
				grid grid-cols-[auto_1fr] gap-3 group/question
				${!isRequired && 'mt-8'}
				${index === 0 ? 'pt-0' : 'pt-8'}
			`)}
		>
			<div className="relative">
				{dragHandle}
				<div
					style={{
						backgroundColor: backgroundColor,
						color: getContrastingTextColor(backgroundColor),
					}}
					className="text-sm font-bold rounded-full h-8 w-8 flex items-center justify-center"
				>
					{index + 1}
				</div>
			</div>
			<div className="pt-1 flex flex-col">
				{!isRequired && (
					<div className="flex flex-wrap items-center gap-2 mb-3">
						<WidgetIconWrapper
							icon={<Glyphs.core.TypeIcon />}
						>
							<WidgetInputQuickSwitch
								placeholder={t('formBuilder.labels.type')}
								options={QUESTION_TYPE_OPTIONS}
								value={question.type === 'open_text'
									? 'open_text'
									: question.subtype === 'multiple'
									? 'select_multiple'
									: 'select'}
								onChange={(value) => handleQuestionTypeChange(question.id, value)}
								label={t('formBuilder.labels.type')}
							/>
						</WidgetIconWrapper>
						{question.type !== 'open_text' && (
							<WidgetIconWrapper
								icon={<QuestionWeightIcon />}
							>
								<WidgetInputQuickSwitch
									placeholder={t('formBuilder.labels.weight')}
									options={WEIGHT_OPTIONS}
									value={question.weight?.toString() || '1'}
									onChange={(value) => {
										const updatedQuestion = { ...question, weight: parseInt(value) }
										handleQuestionWeightChange(question.id, updatedQuestion)
									}}
									label={t('formBuilder.labels.weight')}
								/>
							</WidgetIconWrapper>
						)}
						<WidgetIconWrapper
							icon={<RequiredIcon />}
						>
							<WidgetInputQuickSwitch
								placeholder={t('formBuilder.labels.required')}
								options={REQUIRED_OPTIONS}
								value={question.validationRules?.includes('required') ? 'yes' : 'no'}
								onChange={(value) => handleRequiredChange(question.id, value === 'yes')}
								label={t('formBuilder.labels.required')}
							/>
						</WidgetIconWrapper>
						{question.subtype === 'multiple' && (() => {
							const defaultMax = String(question.options?.length ?? 1)
							const maxRule = question.validationRules?.find((r) => r.startsWith('max:'))
							const parsedMax = maxRule ? parseInt(maxRule.split(':')[1], 10) : NaN
							const maxValue = Number.isFinite(parsedMax) ? String(parsedMax) : defaultMax

							return (
								<WidgetIconWrapper
									icon={<MaxSelectionsIcon />}
								>
									<WidgetInputQuickSwitch
										placeholder={t('formBuilder.labels.maxSelections')}
										options={generateMaxSelectionsOptions(question.options?.length ?? 1)}
										value={maxValue}
										onChange={(value) => {
											const parsed = parseInt(value, 10)
											handleMaxSelectionsChange(
												question.id,
												Number.isFinite(parsed) ? parsed : (question.options?.length ?? 1),
											)
										}}
										label={t('formBuilder.labels.maxSelections')}
									/>
								</WidgetIconWrapper>
							)
						})()}
					</div>
				)}
				<div className="flex items-center">
					<div className="flex-1">
						<div className="font-semibold text-16 text-slate-700">
							<LiveField
								fitSize={false}
								value={question.label}
								onChange={(value) => handleLabelChange(question.id, value)}
							/>
						</div>
						{isRequired && (
							<div className="text-sm text-grey-600 mt-1">
								{i18next.t('editor:simulator.form.required')}
							</div>
						)}
						{question.attribute === 'contact_consent' && question.options && (() => {
							const consentTypes = question.options
								.map((option) => option.consentType)
								.filter(Boolean)

							const hasPositive = consentTypes.includes('positive')
							const hasNegative = consentTypes.includes('negative')

							if (!hasPositive || !hasNegative) {
								const missing: string[] = []
								if (!hasPositive) { missing.push('"Yes"') }
								if (!hasNegative) { missing.push('"No"') }

								return (
									<div className="text-sm text-red-600 mt-1">
										Missing required {missing.join(' and ')} option{missing.length > 1 ? 's' : ''}
									</div>
								)
							}

							return null
						})()}
					</div>
					{!isRequired
						&& (
							<button
								className="text-red-500 text-sm hover:bg-red-100 px-2.5 py-1 rounded cursor-pointer opacity-0 group-hover/question:opacity-100 transition-opacity"
								onClick={onDeleteQuestion}
							>
								&minus;
							</button>
						)}
				</div>
			</div>
			{['name', 'email', 'phone', 'text'].includes(question.type) && (
				<>
					<div />
					<div className="h-10 w-full border border-grey-200 rounded" />
				</>
			)}
			{question.type === 'open_text' && (
				<>
					<div />
					<div className="h-24 w-full border border-grey-200 rounded resize-y bg-grey-50" />
				</>
			)}
			{question.type === 'select' && (
				<>
					<div className="grid gap-1 ml-1 relative col-span-full">
						<div className="ml-8 h-3 relative">
							<NewOptionButton onClick={onAddOptionTop} />
						</div>
						<DragDropContext onDragEnd={handleOptionDragEnd}>
							<Droppable
								droppableId={question.id}
								type="OPTION"
								renderClone={(provided, snapshot, rubric) => (
									<div
										{...provided.draggableProps}
										{...provided.dragHandleProps}
										ref={provided.innerRef}
									>
										<div className="flex flex-row items-start gap-3 relative">
											<div className="mt-0.5 ml-3 size-5 text-grey-500 flex items-center justify-center">
												<DragHandle />
											</div>
											<div className="relative pt-1">
												<div
													className={`size-4 ring-2 ring-purple-400 ${
														question.subtype === 'multiple' ? 'rounded-sm' : 'rounded-full'
													}`}
												/>
											</div>
											<LiveField
												fitSize={false}
												value={question.options?.[rubric.source.index]?.label ?? ''}
												onChange={(value) =>
													handleOptionLabelChange(
														question.id,
														question.options?.[rubric.source.index]?.id ?? '',
														value,
													)}
											/>
										</div>
									</div>
								)}
							>
								{(provided, snapshot) => (
									<div ref={provided.innerRef} {...provided.droppableProps}>
										{question.options?.map((option, optionIndex) => (
											<Draggable key={option.id} draggableId={option.id} index={optionIndex}>
												{(provided, snapshot) => (
													<OptionRow
														question={question}
														option={option}
														optionIndex={optionIndex}
														backgroundColor={backgroundColor}
														getItemStyle={getItemStyle}
														provided={provided}
														snapshot={snapshot}
														handleOptionLabelChange={handleOptionLabelChange}
														handleOptionDelete={handleOptionDelete}
														handleOptionScoreChange={handleOptionScoreChange}
														handleOptionEligibilityChange={handleOptionEligibilityChange}
														handleOptionConsentTypeChange={handleOptionConsentTypeChange}
														handleNewOption={handleNewOption}
													/>
												)}
											</Draggable>
										))}
										{provided.placeholder}
									</div>
								)}
							</Droppable>
						</DragDropContext>
					</div>
				</>
			)}

			<div className="h-3 relative col-span-full mt-2">
				<NewQuestionButton onClick={onAddQuestion} />
			</div>
		</div>
	)
})
